import { ArrowUpRight, Film, Megaphone, Sparkles } from "lucide-react";

const ITEMS = [
    {
        kicker: "We Build",
        title: "Cinema",
        icon: Film,
        body: "An AI-augmented studio for cinematic ad films, founder-led documentaries, premium YouTube channels and CGI-driven brand content. We operate in-house — script to screen — at studio standard.",
        bullets: [
            "Brand films & founder narratives",
            "Full-funnel YouTube channel ops",
            "AI + practical VFX pipeline",
        ],
        href: "#services",
        img: "https://images.unsplash.com/photo-1561446289-4112a4f79116?fm=jpg&q=80&w=900&auto=format&fit=crop",
    },
    {
        kicker: "We Build",
        title: "Authority",
        icon: Megaphone,
        body: "An external media department that engineers earned coverage and performance pipeline together. Direct-to-journalist outreach, paid amplification, and quarterly reputation audits — measured against AMEC.",
        bullets: [
            "Direct-to-journalist Producer's Kit",
            "Performance buys for high-ticket B2B",
            "Reputation index, board-ready",
        ],
        href: "#services",
        img: "https://images.unsplash.com/photo-1744135570217-067335d24c22?fm=jpg&q=65&w=700&auto=format&fit=crop",
    },
    {
        kicker: "We Build",
        title: "Generative Reach",
        icon: Sparkles,
        body: "Our proprietary GEO framework engineers your brand to be co-cited inside ChatGPT, Claude, Perplexity and Gemini answers — the new front door of discovery, owned by entity, not by keyword.",
        bullets: [
            "Co-citation strategy",
            "Semantic anchoring & entity SEO",
            "Editorial saturation across verified sources",
        ],
        href: "#geo",
        img: "https://images.unsplash.com/photo-1647681707526-ba84dcc62ed9?fm=jpg&q=65&w=700&auto=format&fit=crop",
    },
];

export default function Ecosystem() {
    return (
        <section
            id="ecosystem"
            data-testid="ecosystem-section"
            className="relative py-28 md:py-40 bg-[#050505]"
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-10">
                {/* Section header */}
                <div className="grid md:grid-cols-12 gap-10 mb-16 reveal">
                    <div className="md:col-span-7">
                        <div className="text-[11px] uppercase tracking-[0.35em] text-[#e6192b] mb-6">
                            ⎯⎯ Ecosystem
                        </div>
                        <h2
                            data-testid="ecosystem-headline"
                            className="font-display-tight text-[clamp(2.5rem,7vw,7rem)] text-balance"
                        >
                            What we
                            <br />
                            <span className="text-[#a0a0ab]">build.</span>
                        </h2>
                    </div>
                    <p className="md:col-span-5 self-end text-[#a0a0ab] leading-relaxed text-[15px] md:text-base">
                        Three interlocking studios. One operating system for
                        compounding brand authority — designed for the
                        AI-First Era and engineered for high-ticket B2B sales
                        cycles.
                    </p>
                </div>

                {/* Cards */}
                <div className="grid md:grid-cols-3 gap-px bg-[#1c1c1f] border border-[#1c1c1f]">
                    {ITEMS.map(({ kicker, title, icon: Icon, body, bullets, href, img }, i) => (
                        <a
                            key={title}
                            href={href}
                            data-testid={`ecosystem-card-${i}`}
                            className="group relative block bg-[#050505] hover:bg-[#0a0a0c] transition-colors duration-500 overflow-hidden reveal"
                            style={{ transitionDelay: `${i * 90}ms` }}
                        >
                            {/* Image */}
                            <div className="relative aspect-[16/10] overflow-hidden">
                                <img
                                    src={img}
                                    alt={title}
                                    loading="lazy"
                                    decoding="async"
                                    className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-[1200ms] ease-out"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent" />
                                <div className="absolute top-4 left-4 flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-white/85">
                                    <span className="h-1.5 w-1.5 rounded-full bg-[#e6192b]" />
                                    0{i + 1} · {kicker}
                                </div>
                                <Icon
                                    size={22}
                                    strokeWidth={1.2}
                                    className="absolute top-4 right-4 text-white/70 group-hover:text-[#e6192b] transition-colors"
                                />
                            </div>

                            {/* Content */}
                            <div className="p-7 md:p-9">
                                <div className="flex items-baseline gap-3 mb-5">
                                    <span className="text-[11px] uppercase tracking-[0.22em] text-[#e6192b]">
                                        {kicker}
                                    </span>
                                </div>
                                <h3 className="font-display-tight text-[clamp(2rem,3.4vw,3rem)]">
                                    {title}
                                </h3>
                                <p className="mt-5 text-[#a0a0ab] leading-relaxed text-[14.5px]">
                                    {body}
                                </p>
                                <ul className="mt-6 space-y-2">
                                    {bullets.map((b) => (
                                        <li
                                            key={b}
                                            className="flex items-start gap-2.5 text-[13px] text-[#cfcfd6]"
                                        >
                                            <span className="mt-2 h-px w-3 bg-[#e6192b] flex-shrink-0" />
                                            {b}
                                        </li>
                                    ))}
                                </ul>
                                <div className="mt-8 flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-white group-hover:text-[#e6192b] transition-colors">
                                    Learn more
                                    <ArrowUpRight
                                        size={14}
                                        className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                                    />
                                </div>
                            </div>
                        </a>
                    ))}
                </div>
            </div>
        </section>
    );
}
